import { forwardRef } from 'react';
import styled from 'styled-components';
import type { TranslationType } from '@kiwicom/account-consts';
import { FormattedMessage } from 'react-intl';

import Text from '../Text';
import InclusivelyHidden from '../InclusivelyHidden';

type ChoiceTileProps = {
  readonly children: TranslationType;
  readonly id: string;
  readonly currentValue?: string;
  readonly onValueChange?: (value: string) => void;
  readonly value?: string;
  readonly dataTest?: string;
};

type WrapperProps = {
  dataTest: string;
  isSelected: boolean;
};

const Wrapper = styled.button.attrs<WrapperProps>(({ dataTest }) => ({
  'data-test': dataTest,
}))<WrapperProps>`
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.orbit.paletteWhite};
  box-shadow: ${({ theme }) => theme.orbit.elevationFixedBoxShadow};
  border-radius: ${({ theme }) => theme.orbit.borderRadius100};
  height: 44px;
  border: ${({ theme, isSelected }) => {
    return `solid 2px ${isSelected ? theme.orbit.paletteProductNormal : theme.orbit.paletteWhite}`;
  }};
  width: 100%;
  flex: 1;
  cursor: pointer;
  outline: none;
  :focus,
  :active {
    box-shadow: ${({ theme }) => theme.orbit.elevationActionActive};
  }
`;

const ChoiceTile = (
  { children, currentValue, onValueChange, value, dataTest, id }: ChoiceTileProps,
  ref,
): JSX.Element => {
  const onClick = () => {
    if (onValueChange != null && value != null) {
      onValueChange(value);
    }
  };

  const isSelected = currentValue === value;
  return (
    <Wrapper
      aria-describedby={id}
      ref={ref}
      isSelected={isSelected}
      onClick={onClick}
      dataTest={dataTest}
      type="button"
    >
      <Text>{children}</Text>
      {isSelected && (
        <InclusivelyHidden id={id}>
          <FormattedMessage
            id="account_v2.components.choice_tile.selected"
            defaultMessage="Selected"
          />
        </InclusivelyHidden>
      )}
    </Wrapper>
  );
};

export default forwardRef<HTMLButtonElement, ChoiceTileProps>(ChoiceTile);
